import { isTauri } from "@/utils";
import { onActiveRootChange } from "@/services/drives";
import type { ConversationRepository } from "./ConversationRepository";
import type { MemoryRepository } from "./MemoryRepository";
import type { ConfigRepository } from "./ConfigRepository";
import { tauriStoreConversationRepository } from "./tauriStoreConversationRepository";
import { localConversationRepository } from "./localConversationRepository";
import { sqliteConversationRepository } from "./sqlite/sqliteConversationRepository";
import { sqliteMemoryRepository } from "./sqlite/sqliteMemoryRepository";
import { migrateFromTauriStoreIfNeeded } from "./sqlite/migration";
import { driveConfigRepository } from "./driveConfigRepository";

export type { ConversationRepository } from "./ConversationRepository";
export type { MemoryRepository } from "./MemoryRepository";
export type { ConfigRepository } from "./ConfigRepository";

let migrationPromise: Promise<void> | null = null;

/** Runs (and memoizes) the one-time legacy-store migration before the first
 * SQLite read/write against the currently active database. Reset whenever
 * the active root changes, since a new root means a different (possibly
 * unmigrated) database file. */
function ensureMigrated(): Promise<void> {
  migrationPromise ??= migrateFromTauriStoreIfNeeded();
  return migrationPromise;
}

onActiveRootChange(() => {
  migrationPromise = null;
});

/**
 * Returns the process-wide conversation repository — SQLite (via
 * `@tauri-apps/plugin-sql`) inside the native shell, migrated transparently
 * from Phase 2's tauri-store JSON on first use; `localStorage` when running
 * as a plain browser dev server. Callers never branch on environment or on
 * whether a Seufic Drive is connected — this is the one seam that does,
 * and the underlying SQLite connection itself re-resolves to whichever root
 * is active (see `services/drives/paths.ts` and `sqlite/connection.ts`).
 */
export function getConversationRepository(): ConversationRepository {
  if (!isTauri()) return localConversationRepository;
  return {
    async list() {
      await ensureMigrated();
      return sqliteConversationRepository.list();
    },
    async get(id) {
      await ensureMigrated();
      return sqliteConversationRepository.get(id);
    },
    async save(conversation) {
      await ensureMigrated();
      return sqliteConversationRepository.save(conversation);
    },
    async delete(id) {
      await ensureMigrated();
      return sqliteConversationRepository.delete(id);
    },
  };
}

/** The legacy Phase 2 store — kept reachable only as the migration source
 * (see `sqlite/migration.ts`); nothing else should write through it. */
export { tauriStoreConversationRepository };

/** Memory storage — SQLite-backed, same database as conversations. Not
 * available outside Tauri; `MemoryPage` treats an empty/unavailable
 * repository as "no memories yet" rather than erroring. */
export function getMemoryRepository(): MemoryRepository | null {
  return isTauri() ? sqliteMemoryRepository : null;
}

/** Configuration storage — mirrors settings onto the active root's
 * `Config/settings.json`. `localStorage` (via the settings store's own
 * `persist` middleware) remains the source of truth read at startup; this
 * is the write-through copy that makes config portable with the drive. */
export function getConfigRepository(): ConfigRepository | null {
  return isTauri() ? driveConfigRepository : null;
}
